import React, { useState, useRef, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { REST_API_URL } from "../../api/config";

const BASE_URL = REST_API_URL;

const getUserTimeZone = () => {
  return Intl.DateTimeFormat().resolvedOptions().timeZone;
};

const buildHeaders = (token, additionalHeaders = {}) => {
  const headers = {
    "Content-Type": "application/json",
    ...additionalHeaders,
  };
  
  if (token) {
    headers["Authorization"] = `Bearer ${token}`;
  }
  
  const timezone = getUserTimeZone();
  if (timezone) {
    headers["User-Timezone"] = timezone;
  }
  
  return headers;
};

export default function DeleteAccountModal({ closeModal }) {
  const [step, setStep] = useState(1);
  const [reason, setReason] = useState("Personal reasons");
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [customReason, setCustomReason] = useState("");
  const [confirmText, setConfirmText] = useState("");
  const [deleteSuccess, setDeleteSuccess] = useState(false);
  const [userEmail, setUserEmail] = useState("");
  const modalRef = useRef(null);

  const getToken = () => {
    if (typeof window !== "undefined") {
      return (
        localStorage.getItem("accessToken") ||
        sessionStorage.getItem("accessToken")
      );
    }
    return null;
  };

  useEffect(() => {
    const token = getToken();
    if (!token) return;

    (async () => {
      try {
        const headers = buildHeaders(token);
        
        const res = await fetch(
          `${REST_API_URL}/api/auth/userinfo/`,
          { headers }
        );

        const data = await res.json();

        if (data?.user?.email) {
          setUserEmail(data.user.email);
        }
      } catch (err) {
        console.error("Failed to load userinfo", err);
      }
    })();
  }, []);

  useEffect(() => {
    function handleClickOutside(e) {
      if (modalRef.current && !modalRef.current.contains(e.target)) {
        closeModal();
        setStep(1);
      }
    }
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [closeModal]);

  const reasons = [
    "Personal reasons",
    "I have privacy or security concerns",
    "I'm taking a break from this platform",
    "It's too expensive",
    "I found a better alternative",
    "I created this account by mistake",
    "Other",
  ];

  const modalVariants = {
    hidden: { opacity: 0, scale: 0.97 },
    visible: { opacity: 1, scale: 1 },
    exit: { opacity: 0, scale: 0.97 },
  };

  const handleFinalDelete = async () => {
    const token = getToken();
    const finalReason = reason === "Other" ? customReason : reason;

    try {
      const headers = buildHeaders(token);

      const response = await fetch(
        `${REST_API_URL}/api/auth/account-delete/`,
        {
          method: "DELETE",
          headers,
          body: JSON.stringify({
            email: userEmail,
            password: "DELETE",
            reason: finalReason,
          }),
        }
      );

      const data = await response.json();

      if (response.ok) {
        setDeleteSuccess(true);
        setStep(4);
      } else {
        alert(JSON.stringify(data, null, 2));
      }
    } catch (err) {
      alert("Network error deleting account");
    }
  };

  return (
    <AnimatePresence>
      <motion.div
        className="fixed inset-0 bg-black/50 backdrop-blur-sm flex justify-center items-center z-50"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
      >
        <motion.div
          ref={modalRef}
          variants={modalVariants}
          initial="hidden"
          animate="visible"
          exit="exit"
          transition={{ duration: 0.2 }}
          className="bg-white w-[435px] p-6 rounded-2xl shadow-xl"
        >
          {step === 1 && (
            <motion.div
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -10 }}
            >
              <h2 className="text-[20px] font-bold mb-1">Delete Account</h2>

              <p className="text-[#6A6A6A] mb-4 text-[14px]">
                Please let us know why you're choosing to delete your account
              </p>

              <label className="text-[13px] text-[#9A99AB]">
                Reason for Deleting
              </label>

              <div className="relative mt-1">
                <div
                  onClick={() => setDropdownOpen(!dropdownOpen)}
                  className="
          bg-[#FAFAFA]
          border border-[rgba(154,153,171,0.4)]
          rounded-[14px]
          h-[54px]
          px-4
          flex justify-between items-center
          cursor-pointer
        "
                >
                  <span className="text-[15px] text-[#302B2B]">{reason}</span>

                  <span
                    className="
          w-3 h-3 border-r-2 border-t-2 border-[#9A99AB]
          rotate-45
        "
                  />
                </div>

                <AnimatePresence>
                  {dropdownOpen && (
                    <motion.div
                      initial={{ opacity: 0, y: -6 }}
                      animate={{ opacity: 1, y: 0 }}
                      exit={{ opacity: 0, y: -6 }}
                      transition={{ duration: 0.15 }}
                      className="
              absolute left-0 top-[60px]
              w-full
              bg-white
              border border-[rgba(154,153,171,0.2)]
              shadow-[0_4px_6px_rgba(0,0,0,0.04)]
              rounded-[16px]
              p-5
              flex flex-col gap-5
              z-[999]
            "
                    >
                      {reasons.map((item, index) => (
                        <div
                          key={index}
                          onClick={() => {
                            setReason(item);
                            setDropdownOpen(false);
                            if (item === "Other") setStep(2);
                          }}
                          className="
                  flex justify-between items-center
                  cursor-pointer
                "
                        >
                          <span className="text-[16px] text-[#302B2B]">
                            {item}
                          </span>

                          <div className="relative w-[24px] h-[24px]">
                            {reason === item ? (
                              <div className="bg-[#5563F6] w-[22px] h-[22px] rounded-full flex justify-center items-center">
                                <span className="text-white text-[14px] leading-[22px] h-full">
                                  ●
                                </span>
                              </div>
                            ) : (
                              <div className="w-[22px] h-[22px] rounded-full border border-[#9A99AB]" />
                            )}
                          </div>
                        </div>
                      ))}
                    </motion.div>
                  )}
                </AnimatePresence>
              </div>

              <div className="flex justify-end gap-4 mt-6">
                <button className="text-[#6A6A6A]" onClick={closeModal}>
                  Cancel
                </button>

                <button
                  className="bg-[#E1090C] text-white px-6 py-2 rounded-[14px]"
                  onClick={() => setStep(3)}
                >
                  Continue
                </button>
              </div>
            </motion.div>
          )}

          {step === 2 && (
            <motion.div
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.25 }}
            >
              <h2 className="text-[20px] font-bold mb-1 text-[#302B2B]">
                Delete Account
              </h2>

              <p className="text-[#9A99AB] text-[14px] mb-5">
                Please let us know why you're choosing to delete your account
              </p>

              <label className="text-sm text-[#302B2B] mb-1 block">
                Reason for Deleting
              </label>

              <div className="relative mb-6">
                <div
                  className="
          border border-[#D8D8E2]
          rounded-[14px]
          p-3
          cursor-pointer
          flex justify-between items-center
          bg-white
        "
                  onClick={() => setDropdownOpen(!dropdownOpen)}
                >
                  {reason}
                  <span className="rotate-45 border-r-2 border-t-2 w-3 h-3 text-[#9A99AB]"></span>
                </div>

                {dropdownOpen && (
                  <motion.div
                    initial={{ opacity: 0, y: -5 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: -5 }}
                    className="
            absolute left-0 right-0 mt-1 
            bg-white border border-[#D8D8E2]
            rounded-[16px] shadow-[0_4px_6px_rgba(0,0,0,0.04)]
            z-20 py-2
          "
                  >
                    {reasons.map((item) => (
                      <div
                        key={item}
                        className="
                px-4 py-2 
                hover:bg-[#F5F6FA] 
                cursor-pointer 
                flex justify-between 
                text-sm text-[#302B2B]
              "
                        onClick={() => {
                          setReason(item);
                          setDropdownOpen(false);

                          if (item !== "Other") {
                            setCustomReason("");
                          }
                        }}
                      >
                        {item}
                        {reason === item && (
                          <span className="w-3 h-3 bg-[#5563F6] rounded-full"></span>
                        )}
                      </div>
                    ))}
                  </motion.div>
                )}
              </div>

              {reason === "Other" && (
                <motion.div
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.25 }}
                >
                  <label className="text-sm text-[#302B2B] block mb-1">
                    Your reason for deleting account
                  </label>

                  <textarea
                    className="
            w-full 
            border border-[#D8D8E2] 
            rounded-[14px] 
            bg-white 
            p-4 
            text-[15px]
            h-[140px]
            placeholder:text-[#C4C4C4]
            focus:outline-[#5563F6]
          "
                    placeholder="Type your reason for deleting account (optional)"
                    onChange={(e) => setCustomReason(e.target.value)}
                  />
                </motion.div>
              )}

              <div className="flex justify-end items-center gap-6 mt-6">
                <button
                  className="text-[#9A99AB] text-[15px]"
                  onClick={closeModal}
                >
                  Cancel
                </button>

                <button
                  className="
          bg-[#E1090C]
          text-white
          px-8
          py-3
          rounded-[12px]
          text-[15px]
          font-medium
        "
                  onClick={() => setStep(3)}
                >
                  Continue
                </button>
              </div>
            </motion.div>
          )}

          {step === 3 && (
            <motion.div
              initial={{ opacity: 0, scale: 0.97 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.25 }}
              className="flex flex-col items-center text-center"
            >
              <div className="w-[72px] h-[72px] rounded-full bg-[#FDE8E8] flex items-center justify-center mb-4">
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  className="h-10 w-10 text-[#E1090C]"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M12 9v3m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 17c-.77 1.333.192 3 1.732 3z"
                  />
                </svg>
              </div>

              <h2 className="text-[20px] font-bold text-[#E1090C] leading-tight mb-3">
                WARNING this is permanent and cannot
                <br />
                be undone!
              </h2>

              <p className="text-[#302B2B] text-[15px] mb-6 leading-snug w-full text-start">
                To delete your account, please confirm by
                typing <span className="font-semibold">"DELETE".</span>
              </p>

              <div className="flex w-full items-center justify-center gap-3 mb-7">
                <input
                  className="
          w-[380px] 
          h-[50px] 
          border border-[#D8D8E2] 
          rounded-[12px] 
          px-4
          bg-white
          text-[15px]
          placeholder:text-[#C4C4C4]
          focus:outline-[#5563F6]
        "
                  placeholder="Type here"
                  onChange={(e) => setConfirmText(e.target.value)}
                />

                <button
                  disabled={confirmText !== "DELETE"}
                  onClick={handleFinalDelete}
                  className={`
          w-[150px] h-[50px] 
          rounded-[12px]
          text-[15px] font-medium
          border-2
          ${
            confirmText === "DELETE"
              ? "border-[#E1090C] text-[#E1090C] bg-white"
              : "border-[#F3B4B4] text-[#F3B4B4] bg-white cursor-not-allowed"
          }
        `}
                >
                  Yes I confirm
                </button>
              </div>

              <button
                onClick={closeModal}
                className="
        w-full h-[56px]
        bg-[#5563F6]
        hover:bg-[#4450D6]
        transition
        text-white text-[16px]
        rounded-[14px]
      "
              >
                Cancel
              </button>
            </motion.div>
          )}

          {step === 4 && deleteSuccess && (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              className="fixed inset-0 bg-[#F4F5FA] flex flex-col items-center justify-center text-center z-[99999]"
            >
              <div className="text-4xl mb-6">:(</div>

              <h2 className="text-2xl font-semibold text-[#2D2D2D] mb-2">
                Your account has been deleted
              </h2>

              <p className="text-[#6B6B6B] mb-6">Sad to see you go</p>

              <button
                className="bg-[#5563F6] text-white px-6 py-3 rounded-xl text-sm"
                onClick={() => (window.location.href = "/")}
              >
                Go to Halper home page
              </button>
            </motion.div>
          )}
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
}